import noop from 'lodash/noop';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { BsIcon } from '@hotelplan/core.basis.bs-icon';
import { IGalleryApi } from '@hotelplan/core.serving.bs-rsv-gallery';
import { FdrImage } from '@hotelplan/fdr.regular.basis.fdr-image';
import { FdrRoute } from '@hotelplan/fdr.regular.basis.fdr-route';
import { useLazyFetchingPagination } from '@hotelplan/libs.hooks';
import { useElementSize, useOnScreen } from '@hotelplan/libs.hooks-dom';
import { FdrProductType } from '@hotelplan/supergraph-api';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import {
  TFdrAdventureTravelGeoProduct,
  TFdrThGeoModuleObject,
} from 'components/domain/fdr-geo/fdr-geo.types';
import {
  createGeoProductVariables,
  getFdrGeoProductGalleryProducts,
  mapFdrGeoProductResponseToAdventureTravels,
} from 'components/domain/fdr-geo/fdr-geo.utils';
import { NavigationIds } from 'components/domain/fdr-geo/navigation/fdr-geo-navigation.types';
import useFdrPageComponentsFetch from 'components/domain/fdr-page-components/useFdrPageComponentsFetch';
import { mapFdrMediaToImageMediaItems } from 'components/domain/fdr-pdp-media-gallery';
import {
  trackMediaGalleryFullScreenClick,
  trackMediaGalleryLinkClick,
} from 'components/domain/tracking/mediaGalleryTracking';
import { renderMedia } from 'components/util/criterias';
import { FdrGeoProductsDocument } from 'schemas/query/geo/fdr-geo-products.generated';
import FdrGeoProductGallerySlideCount from './fdr-geo-product-gallery-slide-count';
import FdrGeoProductGallerySlideInfo from './fdr-geo-product-gallery-slide-info';
import {
  ProductDescription,
  ProductGalleryContent,
  ProductGalleryPreviewContainer,
  ProductGalleryPreviewItem,
  ProductGalleryPreviewItemImage,
  ProductGalleryPreviewItemList,
  ProductGalleryPreviewItemText,
  ProductGalleryPreviewItemTextWrapper,
  ProductGalleryWrapper,
  ProductLink,
  ProductMediaGalleryContainer,
  ProductNameTitle,
  ProductSlideFooter,
  ProductSlideHeader,
  ResponsiveProductMediaGallery,
  SectionMainTitle,
} from './fdr-geo-product-gallery.styles';

const Loader = styled.span(
  sx2CssThemeFn({
    position: ['absolute', 'static'],
    right: ['10px', 'initial'],
    top: ['-30px', 'initial'],
    fontSize: 2,
    color: 'white',
  })
);

const PRODUCTS_PER_PAGE = 9;

interface IProductGalleryProps {
  id: string;
  products: TFdrThGeoModuleObject['products'];
  total: number;
  geoObject: string;
  title?: string;
}

const FdrGeoProductGallery: React.FC<IProductGalleryProps> = ({
  id,
  products,
  total,
  geoObject,
}) => {
  const [t] = useTranslation(['geo', 'search']);
  const galleryContainerRef = useRef<HTMLDivElement>(null);
  const slideInfoRef = useRef<HTMLDivElement>(null);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const galleryApiRef = useRef<IGalleryApi>({ setSlide: noop });

  const { h: galleryHeight } = useElementSize(galleryContainerRef, {
    throttle: 100,
  });
  const { h: slideInfoHeight } = useElementSize(slideInfoRef, {
    throttle: 100,
  });

  const mapVariables = useCallback(
    (page, perPage) => {
      return createGeoProductVariables(id, page, perPage, [
        FdrProductType.AdventureTravel,
      ]);
    },
    [id]
  );

  const { fetchItems, loading } = useFdrPageComponentsFetch(
    FdrGeoProductsDocument,
    mapVariables,
    mapFdrGeoProductResponseToAdventureTravels
  );

  const { items, hasMore, nextPage } =
    useLazyFetchingPagination<TFdrAdventureTravelGeoProduct>(
      getFdrGeoProductGalleryProducts(products),
      {
        perPage: PRODUCTS_PER_PAGE,
        total,
        fetchItems,
      }
    );

  const loadMoreElement = useRef<HTMLDivElement>();

  const { isOnScreen } = useOnScreen(loadMoreElement);

  useEffect(() => {
    if (isOnScreen && !loading && hasMore) {
      nextPage();
    }
  }, [isOnScreen, hasMore, loading]);

  const currentProduct = items[currentProductIndex];

  if (!currentProduct?.images?.length) {
    return null;
  }

  const mediaGalleryItems = mapFdrMediaToImageMediaItems(currentProduct.images);

  return (
    <ProductGalleryWrapper id={NavigationIds.MEDIA}>
      <SectionMainTitle>
        {t('geo:gallery.title', { geoObject })}
      </SectionMainTitle>
      <ProductGalleryContent slideInfoHeight={slideInfoHeight}>
        <ProductGalleryPreviewContainer>
          <ProductGalleryPreviewItemList galleryHeight={galleryHeight}>
            {items.map((item, index) => {
              return (
                <ProductGalleryPreviewItem
                  key={index}
                  href={'#'}
                  onClick={event => {
                    event.preventDefault();
                    setCurrentProductIndex(index);
                    galleryApiRef.current.setSlide(0);
                  }}
                  className={currentProductIndex === index ? 'active' : null}
                >
                  <ProductGalleryPreviewItemImage>
                    <FdrImage image={item.headerImages[0]} />
                  </ProductGalleryPreviewItemImage>
                  <ProductGalleryPreviewItemTextWrapper>
                    <ProductGalleryPreviewItemText>
                      {item.name}
                    </ProductGalleryPreviewItemText>
                  </ProductGalleryPreviewItemTextWrapper>
                </ProductGalleryPreviewItem>
              );
            })}
            <div ref={loadMoreElement}>
              {loading && <Loader>{t('search:loading')}</Loader>}
            </div>
          </ProductGalleryPreviewItemList>
        </ProductGalleryPreviewContainer>
        <ProductMediaGalleryContainer>
          <div ref={galleryContainerRef}>
            <ResponsiveProductMediaGallery
              galleryApi={galleryApiRef}
              renderSlide={renderMedia}
              galleryHeight={galleryHeight}
              previewIconName={`open-gallery`}
              items={mediaGalleryItems}
              afterOverlayOpen={trackMediaGalleryFullScreenClick}
              overlaySlideInfo={(item, slideIndex, count) => (
                <>
                  <ProductSlideHeader className="overlay">
                    <span className="slide-count">
                      {slideIndex + 1} | {count}
                    </span>
                  </ProductSlideHeader>
                  <div className={`slide-info`}>
                    <ProductSlideFooter className="overlay">
                      <FdrRoute link={currentProduct.webMeta.meta.link}>
                        <ProductLink onClick={trackMediaGalleryLinkClick}>
                          <BsIcon name={'chevron-long-right'} size={'lg'} />
                          <ProductNameTitle>
                            {currentProduct.name}
                          </ProductNameTitle>
                        </ProductLink>
                      </FdrRoute>
                      <ProductDescription className="product-description">
                        {item.title && (
                          <span className="slide-title">{item.title}</span>
                        )}
                        {item.image.description && (
                          <span className="slide-description">
                            {item.image.description}
                          </span>
                        )}
                      </ProductDescription>
                    </ProductSlideFooter>
                  </div>
                </>
              )}
              slideInfo={(item, slideIndex, count, isHideBackground) => (
                <>
                  <FdrGeoProductGallerySlideCount
                    item={item}
                    slide={slideIndex + 1}
                    count={count}
                  />
                  <div className={`slide-info`} ref={slideInfoRef}>
                    <ProductSlideFooter>
                      <FdrRoute link={currentProduct.webMeta.meta.link}>
                        <ProductLink onClick={trackMediaGalleryLinkClick}>
                          <BsIcon name={'chevron-long-right'} size={'lg'} />
                          <ProductNameTitle>
                            {currentProduct.name}
                          </ProductNameTitle>
                        </ProductLink>
                      </FdrRoute>
                      <FdrGeoProductGallerySlideInfo
                        className="product-slide-info"
                        item={item}
                        isHideBackground={isHideBackground}
                      />
                    </ProductSlideFooter>
                  </div>
                </>
              )}
            />
          </div>
        </ProductMediaGalleryContainer>
      </ProductGalleryContent>
    </ProductGalleryWrapper>
  );
};

export default FdrGeoProductGallery;
